/**
 * BeforeAfterView — comparateur « Avant / Après » sur la zone figée.
 * Avant = snapshot satellite réel · Après = rendu IA. Glissière draggable
 * (clip-path) — pensé pour les rendez-vous client.
 */
import { useCallback, useRef, useState } from 'react';
import { X } from 'lucide-react';

export const BeforeAfterView = ({ beforeSrc, afterSrc, onClose }) => {
  const [pos, setPos] = useState(50);           // % de révélation
  const [afterOk, setAfterOk] = useState(true);
  const [beforeOk, setBeforeOk] = useState(true);
  const wrapRef = useRef(null);
  const dragging = useRef(false);

  const updateFromEvent = useCallback((clientX) => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r) return;
    setPos(Math.min(100, Math.max(0, ((clientX - r.left) / r.width) * 100)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    updateFromEvent(e.clientX);
    const move = (ev) => dragging.current && updateFromEvent(ev.clientX);
    const up = () => {
      dragging.current = false;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <div className="absolute inset-0 z-[530] flex items-center justify-center"
         style={{ background: 'rgba(6,7,10,0.82)', backdropFilter: 'blur(6px)' }}
         data-testid="before-after-view">
      <button type="button" onClick={onClose}
              className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(10,10,13,0.9)', border: '1px solid rgba(255,255,255,0.15)' }}
              data-testid="before-after-close">
        <X className="w-4 h-4 text-gray-300" />
      </button>

      <div ref={wrapRef}
           className="relative select-none cursor-ew-resize rounded-xl overflow-hidden"
           style={{ width: 'min(76vw, 960px)', aspectRatio: '4/3', border: '1px solid var(--c-border-accent)', boxShadow: '0 30px 80px rgba(0,0,0,0.6)' }}
           onPointerDown={onPointerDown}
           data-testid="before-after-stage">
        {/* AVANT — satellite réel */}
        {beforeOk ? (
          <img src={beforeSrc} alt="Avant — satellite" draggable={false}
               onError={() => setBeforeOk(false)}
               className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[12px] text-gray-500"
               style={{ background: '#14171c' }}>
            Snapshot satellite indisponible
          </div>
        )}
        {/* APRÈS — rendu IA, révélé par la glissière */}
        {afterOk && (
          <img src={afterSrc} alt="Après — projet" draggable={false}
               onError={() => setAfterOk(false)}
               className="absolute inset-0 w-full h-full object-cover"
               style={{ clipPath: `inset(0 0 0 ${pos}%)` }} />
        )}
        {/* Glissière */}
        <div className="absolute top-0 bottom-0 pointer-events-none" style={{ left: `${pos}%` }}
             data-testid="before-after-divider">
          <div className="w-[2px] h-full mx-auto" style={{ background: 'var(--c-gold)' }} />
          <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-black"
               style={{ background: 'var(--c-gold)', color: '#0A0A0D', boxShadow: '0 4px 16px rgba(0,0,0,0.5)' }}>
            ⇆
          </div>
        </div>
        {/* Labels */}
        <span className="absolute top-3 left-3 px-2 py-0.5 rounded text-[10px] font-bold tracking-[0.15em] uppercase"
              style={{ background: 'rgba(10,10,13,0.8)', color: '#93C5FD', border: '1px solid rgba(147,197,253,0.35)' }}>
          Avant · satellite
        </span>
        <span className="absolute top-3 right-3 px-2 py-0.5 rounded text-[10px] font-bold tracking-[0.15em] uppercase"
              style={{ background: 'rgba(10,10,13,0.8)', color: 'var(--c-gold)', border: '1px solid var(--c-border-accent)' }}>
          Après · projet
        </span>
      </div>
      <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[11px] text-gray-400">
        Glissez pour révéler le projet sur la vue satellite réelle
      </p>
    </div>
  );
};
